import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Trophy, Crown, Star, Sparkles, Loader2, Save, Calendar,
  Award, Users, MapPin, Briefcase, TrendingUp, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { WinnerWhyDetails } from '../components/WinnerWhyDetails';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join('').toUpperCase();
};

const WinnerHero = ({ winner, label, accentClass, isDark, icon: Icon }) => {
  if (!winner) return null;
  return (
    <Link to={`/performance/${winner.designer_id}`} className="block group">
      <div
        className={cn(
          'rounded-2xl p-6 md:p-8 border-2 transition-all hover:shadow-2xl',
          accentClass,
          isDark ? 'bg-slate-900/40' : 'bg-white'
        )}
        data-testid={`winner-hero-${winner.designer_id}`}
      >
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#331062] via-purple-700 to-[#4a1d8a] flex items-center justify-center text-white text-3xl font-bold shadow-xl">
              {initials(winner.designer_name)}
            </div>
            <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center shadow-lg">
              <Icon className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={cn(
                'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider',
                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
              )}>
                <Sparkles className="w-3 h-3" /> {label}
              </span>
              <span className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                {winner.employee_id} · {winner.designer_role}
              </span>
            </div>
            <h2 className={cn(
              'text-3xl md:text-4xl font-bold font-outfit mt-1 group-hover:text-[#331062] dark:group-hover:text-purple-400 transition-colors',
              isDark ? 'text-white' : 'text-slate-900'
            )}>
              {winner.designer_name}
            </h2>
            <p className={cn('text-sm mt-1 flex flex-wrap gap-3', isDark ? 'text-slate-400' : 'text-slate-500')}>
              <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{winner.sub_team || '—'}</span>
              <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{winner.location || '—'}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{winner.weeks_scored} weeks scored</span>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
              <div className={cn('rounded-lg p-2.5', isDark ? 'bg-slate-800/70' : 'bg-slate-50')}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Composite</div>
                <div className="text-lg font-bold text-[#331062] dark:text-purple-400">{winner.composite_score}</div>
              </div>
              <div className={cn('rounded-lg p-2.5', isDark ? 'bg-slate-800/70' : 'bg-slate-50')}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Avg Ranking</div>
                <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{winner.avg_ranking_score}</div>
              </div>
              <div className={cn('rounded-lg p-2.5', isDark ? 'bg-slate-800/70' : 'bg-slate-50')}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">+ Feedback</div>
                <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{winner.positive_feedback_count}</div>
              </div>
              <div className={cn('rounded-lg p-2.5', isDark ? 'bg-slate-800/70' : 'bg-slate-50')}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Achievements</div>
                <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{winner.approved_achievements}</div>
              </div>
              <div className={cn('rounded-lg p-2.5', isDark ? 'bg-slate-800/70' : 'bg-slate-50')}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Escalations</div>
                <div className="text-lg font-bold text-red-500">
                  {(winner.minor_escalations || 0) + (winner.major_escalations || 0)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

const SubTeamWinnerCard = ({ entry, isDark }) => {
  const w = entry.winner;
  if (!w) return null;
  return (
    <Link to={`/performance/${w.designer_id}`} className="block group">
      <div
        className={cn(
          'rounded-xl p-4 border transition-all hover:shadow-lg hover:-translate-y-0.5',
          isDark ? 'border-slate-700 bg-slate-800/60 hover:bg-slate-800' : 'border-slate-200 bg-white'
        )}
        data-testid={`sub-team-winner-${w.designer_id}`}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#331062] to-[#4a1d8a] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {initials(w.designer_name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className={cn('text-[10px] uppercase tracking-wider font-bold', isDark ? 'text-purple-400' : 'text-[#331062]')}>
              {entry.sub_team}
            </p>
            <p className={cn('font-bold truncate', isDark ? 'text-white' : 'text-slate-800')}>
              {w.designer_name}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {w.employee_id} · {w.designer_role}
            </p>
          </div>
          <Trophy className="w-4 h-4 text-yellow-500 flex-shrink-0" />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className={cn('px-2 py-0.5 rounded-full font-semibold', isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700')}>
            Composite {w.composite_score}
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            +{w.positive_feedback_count} feedback · {w.approved_achievements} ach
          </span>
        </div>
      </div>
    </Link>
  );
};

const Awards = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const canEditNote = user && (user.role === 'admin' || user.role === 'manager');

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;

  const [view, setView] = useState('quarter'); // 'quarter' | 'year'
  const [year, setYear] = useState(String(currentYear));
  const [quarter, setQuarter] = useState(String(currentQuarter));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const fetchAwards = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('arrise_token');
      const endpoint = view === 'quarter'
        ? `${BACKEND_URL}/api/awards/star-of-quarter`
        : `${BACKEND_URL}/api/awards/best-of-year`;
      const params = view === 'quarter'
        ? { year: parseInt(year), quarter: parseInt(quarter) }
        : { year: parseInt(year) };
      const res = await axios.get(endpoint, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setNote(res.data.note || '');
    } catch (err) {
      console.error('Failed to fetch awards:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [view, year, quarter]);

  useEffect(() => { fetchAwards(); }, [fetchAwards]);

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      const token = localStorage.getItem('arrise_token');
      const payload = view === 'quarter'
        ? { award_type: 'star_of_quarter', year: parseInt(year), quarter: parseInt(quarter), note }
        : { award_type: 'best_of_year', year: parseInt(year), note };
      await axios.post(`${BACKEND_URL}/api/awards/note`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Admin note saved');
    } catch (err) {
      console.error('Failed to save note:', err);
      toast.error('Failed to save note');
    } finally {
      setNoteSaving(false);
    }
  };

  const years = [];
  for (let y = currentYear - 2; y <= currentYear; y++) years.push(String(y));

  const award = data?.overall;
  const subTeamWinners = data?.by_sub_team || [];
  const isQuarter = view === 'quarter';
  const heroLabel = isQuarter ? `Star of Q${quarter} ${year}` : `Best Performer of ${year}`;
  const heroAccent = isQuarter
    ? 'border-amber-300 dark:border-amber-700/50 shadow-amber-100 dark:shadow-amber-900/20'
    : 'border-purple-300 dark:border-purple-700/50 shadow-purple-100 dark:shadow-purple-900/20';
  const heroIcon = isQuarter ? Star : Crown;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="awards-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className={cn(
            'text-2xl md:text-3xl font-bold font-outfit flex items-center gap-3',
            isDark ? 'text-white' : 'text-slate-800'
          )}>
            <Trophy className="w-8 h-8 text-yellow-500" />
            Hall of Fame
          </h1>
          <p className={cn('mt-1 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Auto-computed top performers based on composite score = avg ranking + 1.5 × positive feedback + 2 × achievements − 2 × minor escalations − 5 × major escalations
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className={cn('inline-flex rounded-lg p-1', isDark ? 'bg-slate-700' : 'bg-slate-100')}>
            <button
              onClick={() => setView('quarter')}
              data-testid="award-view-quarter"
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                view === 'quarter'
                  ? (isDark ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 shadow')
                  : (isDark ? 'text-slate-300' : 'text-slate-600')
              )}
            >
              <Star className="w-4 h-4 inline mr-1" /> Star of Quarter
            </button>
            <button
              onClick={() => setView('year')}
              data-testid="award-view-year"
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                view === 'year'
                  ? (isDark ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 shadow')
                  : (isDark ? 'text-slate-300' : 'text-slate-600')
              )}
            >
              <Crown className="w-4 h-4 inline mr-1" /> Best of Year
            </button>
          </div>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28" data-testid="award-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          {view === 'quarter' && (
            <Select value={quarter} onValueChange={setQuarter}>
              <SelectTrigger className="w-24" data-testid="award-quarter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['1','2','3','4'].map((q) => <SelectItem key={q} value={q}>Q{q}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-[#331062]" />
        </div>
      ) : !award ? (
        <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Award className="w-16 h-16 text-slate-300 mb-3" />
            <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
              No eligible candidates for this period (designers need at least 2 weeks of scoring per month to qualify).
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Hero winner card */}
          <WinnerHero winner={award} label={heroLabel} accentClass={heroAccent} isDark={isDark} icon={heroIcon} />

          {/* Why This Winner — full breakdown */}
          <WinnerWhyDetails winner={award} details={data?.winner_details} isDark={isDark} />

          {/* Admin note */}
          <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
            <CardHeader className="pb-3">
              <CardTitle className={cn('text-base flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                <Sparkles className="w-4 h-4 text-amber-500" /> Admin Note for This Award
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canEditNote ? (
                <>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Why does this person deserve the award? (visible to all viewers)"
                    rows={3}
                    data-testid="award-note-input"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={saveNote}
                      disabled={noteSaving}
                      className="bg-[#331062] hover:bg-[#4a1d8a]"
                      data-testid="award-note-save"
                    >
                      {noteSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Note
                    </Button>
                  </div>
                </>
              ) : (
                <p className={cn('text-sm italic', isDark ? 'text-slate-300' : 'text-slate-600')}>
                  {note || 'No admin note recorded for this award.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Sub-team winners */}
          {subTeamWinners.length > 0 && (
            <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
              <CardHeader className="pb-3">
                <CardTitle className={cn('flex items-center justify-between text-base', isDark ? 'text-white' : 'text-slate-800')}>
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#331062] dark:text-purple-400" />
                    Sub-Team Winners
                  </span>
                  <span className="text-xs font-normal text-slate-500">{subTeamWinners.length} sub-teams</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {subTeamWinners.map((entry) => (
                    <SubTeamWinnerCard key={entry.sub_team} entry={entry} isDark={isDark} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top candidates leaderboard */}
          {data?.top_candidates?.length > 0 && (
            <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
              <CardHeader className="pb-3">
                <CardTitle className={cn('flex items-center gap-2 text-base', isDark ? 'text-white' : 'text-slate-800')}>
                  <TrendingUp className="w-4 h-4 text-emerald-500" /> Top 10 Candidates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={cn('text-left text-xs uppercase tracking-wider', isDark ? 'text-slate-400' : 'text-slate-500')}>
                        <th className="pb-2 pl-2">#</th>
                        <th className="pb-2">Designer</th>
                        <th className="pb-2">Sub-Team</th>
                        <th className="pb-2 text-right">Composite</th>
                        <th className="pb-2 text-right">Avg Rank</th>
                        <th className="pb-2 text-right">+Fdbk</th>
                        <th className="pb-2 text-right">Ach</th>
                        <th className="pb-2 text-right pr-2">Esc</th>
                      </tr>
                    </thead>
                    <tbody className={isDark ? 'divide-y divide-slate-700' : 'divide-y divide-slate-100'}>
                      {data.top_candidates.map((c, i) => (
                        <tr
                          key={c.designer_id}
                          className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                          data-testid={`top-candidate-row-${c.designer_id}`}
                        >
                          <td className="py-2 pl-2 font-semibold">{i + 1}</td>
                          <td className="py-2">
                            <Link to={`/performance/${c.designer_id}`} className="hover:text-[#331062] dark:hover:text-purple-400">
                              <span className={cn('font-medium', isDark ? 'text-white' : 'text-slate-800')}>{c.designer_name}</span>
                              <p className="text-xs text-slate-500">{c.employee_id} · {c.designer_role}</p>
                            </Link>
                          </td>
                          <td className="py-2 text-xs text-slate-500">{c.sub_team || '-'}</td>
                          <td className="py-2 text-right font-bold text-[#331062] dark:text-purple-400">{c.composite_score}</td>
                          <td className="py-2 text-right">{c.avg_ranking_score}</td>
                          <td className="py-2 text-right text-emerald-600 dark:text-emerald-400">{c.positive_feedback_count}</td>
                          <td className="py-2 text-right text-amber-600 dark:text-amber-400">{c.approved_achievements}</td>
                          <td className="py-2 text-right pr-2 text-red-500">
                            {(c.minor_escalations || 0) + (c.major_escalations || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Awards;
